import { useTerminology, type Terms } from '@/lib/useTerminology'
/**
 * ProjectsPage — die Projektliste im Listen-Standard (AP-35 Detailblatt 1 5.7).
 * Paginiert und gefiltert auf dem Server. Phase K: die übrigen Filter, die
 * Kennzahl-Spalten aus der Nachkalkulation (Detailblatt 8 3.3) und der Export.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings, Check, ArrowUp, ArrowDown, ArrowUpDown, ExternalLink } from 'lucide-react'
import { FilterBar } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { ExportButton } from '@/components/ui/ExportButton'
import { fetchProjectListFigures, fetchProjects, projectsExportRequest } from '../api/projects.api'
import { fetchSites, fetchUsers } from '../api/projects-head.api'
import type { Project, PaginatedProjects, ListProjectsParams, ProjectListFigures, LookupSite, LookupUser } from '../types/project.types'
import type { FilterFieldDef, ActiveFilter } from '@/components/list/list.types';
import { pageWide } from '@/lib/form-styles'
import { PROJECT_STATUSES, PROJECT_TYPES, erfuelltRecht, type ProjectStatus, geldText } from '@symphenia/shared-types'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useAuthStore } from '@/lib/auth.store'
import { ProjectFormDialog } from '../components/ProjectFormDialog'
import { rememberListContext } from '../project-navigation'
import { PROJECT_STATUS_LABELS, PROJECT_TYPE_LABELS, TRAFFIC_LIGHT_CLASSES, TRAFFIC_LIGHT_REASONS, alsTag, istUeberfaellig } from '../project-labels'

/** Detailblatt 1 3.1 — Anzeige und Farbe je Status: eine Stelle (`project-labels.ts`). */
const STATUS_ANZEIGE = PROJECT_STATUS_LABELS

// ─── FilterBar-Felder ─────────────────────────────────────────────────────────

// Phase K: eine Funktion — die Begriffe des Mandanten, die Benutzer und die
// Betriebsstätten stehen erst zur Laufzeit fest (terminologie.md: keine Konstante).
const buildFilterFields = (terms: Terms, users: LookupUser[], sites: LookupSite[]): FilterFieldDef[] => [
  {
    key: 'search',
    label: 'Suche',
    type: 'text',
    category: 'core',
    operators: ['contains', 'not_contains'],
  },
  {
    key: 'status',
    label: 'Status',
    // Phase L: Mehrfachauswahl — die Vorgabe blendet drei Zustände zugleich aus.
    type: 'select-multi',
    category: 'core',
    operators: ['is', 'is_not'],
    // AP-35 Phase B: der Lebenslauf aus Detailblatt 1 3.1.
    options: PROJECT_STATUSES.map((value) => ({ value, label: STATUS_ANZEIGE[value].label })),
  },
  {
    key: 'type',
    label: terms.type,
    type: 'select',
    category: 'core',
    operators: ['is', 'is_not'],
    options: PROJECT_TYPES.map((t) => ({ value: t, label: PROJECT_TYPE_LABELS[t]?.(terms) ?? t })),
  },
  { key: 'clientName', label: 'Kunde', type: 'text', category: 'core', operators: ['contains'] },
  {
    key: 'projectLead',
    label: terms.lead,
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: users.map((u) => ({ value: u.id, label: u.name })),
  },
  {
    key: 'site',
    label: 'Betriebsstätte',
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: sites.map((b) => ({ value: b.id, label: b.name })),
  },
  { key: 'completionFrom', label: 'Fertigstellung ab', type: 'date', category: 'core', operators: ['is'] },
  { key: 'completionTo', label: 'Fertigstellung bis', type: 'date', category: 'core', operators: ['is'] },
  {
    key: 'overdue',
    label: 'Überfällig',
    type: 'boolean',
    category: 'core',
    operators: ['is'],
  },
  {
    key: 'templates',
    label: 'Vorlagen einblenden',
    type: 'boolean',
    category: 'core',
    operators: ['is'],
  },
  {
    key: 'externalSource',
    label: 'Herkunft (Quellsystem)',
    type: 'text',
    category: 'core',
    operators: ['is', 'is_not', 'contains', 'not_contains'],
  },
]

// ─── Spalten ──────────────────────────────────────────────────────────────────

interface ColDef {
  key: string
  label: string
  align?: 'right'
  sortable?: boolean
  /** Phase K — Spalte aus der Nachkalkulation: lädt die Kennzahlen der Seite nach. */
  figure?: boolean
}

/** Phase K — welche Kennzahl-Spalten dieser Benutzer überhaupt bekommt (Blatt 13 4.3). */
interface Darf {
  verkauf: boolean
  kosten: boolean
}

// KA-17: Alle sechs Spalten sortiert jetzt der Server (`buildOrderBy` im
// ProjectsService). Vorher waren nur zwei als sortierbar markiert — und selbst
// die sortierten nicht, weil die Sortierung nie an die API ging.
// AP-16: Die erste Spalte trägt den Begriff des Mandanten — deshalb eine
// Funktion. Eine Konstante steht fest, bevor der Modus geladen ist.
const buildColumns = (terms: Terms, darf: Darf): ColDef[] => [
  { key: 'projectNumber', label: `${terms.number.replace(/nummer$/, 'nr.')}`, sortable: true },
  { key: 'name', label: 'Bezeichnung', sortable: true },
  { key: 'type', label: terms.type, sortable: true },
  { key: 'client', label: 'Kunde', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'projectLead', label: terms.lead },
  { key: 'site', label: 'Betriebsstätte', sortable: true },
  { key: 'completionDueAt', label: 'Fertigstellung bis', sortable: true },
  { key: 'positionCount', label: 'Pos.', align: 'right', sortable: true },
  { key: 'progressPercent', label: 'Leistungsstand %', align: 'right', figure: true },
  ...(darf.verkauf ? [{ key: 'salesTotal', label: 'Verkaufssumme', align: 'right' as const, figure: true }] : []),
  ...(darf.kosten
    ? [
        { key: 'costPlanned', label: `Kosten ${terms.data}`, align: 'right' as const, figure: true },
        { key: 'costActual', label: 'Kosten verbucht', align: 'right' as const, figure: true },
        { key: 'costShare', label: 'Anteil %', align: 'right' as const, figure: true },
        { key: 'margin', label: 'Deckungsbeitrag', align: 'right' as const, figure: true },
      ]
    : []),
  { key: 'trafficLights', label: 'Ampeln', figure: true },
  { key: 'customerOrderNumber', label: 'Kunden-Auftragsnr.', sortable: true },
  { key: 'legacyNumber', label: 'Fremdnummer', sortable: true },
  { key: 'externalSource', label: 'Herkunft', sortable: true },
  { key: 'createdAt', label: 'angelegt am', sortable: true },
]

/** Betrag im Geldformat (`geld.ts`); leer → „—". */
const geld = (n: number | null | undefined) => geldText(n) || '—'

const AMPEL_TITEL: [keyof ProjectListFigures['trafficLights'], string][] = [
  ['schedule', 'Termin'],
  ['cost', 'Kosten'],
  ['progress', 'Leistungsstand'],
]

const COLUMN_LAYOUT_KEY = 'columnbar_layout_projects'

/** Spalten, für die die Seite die Kennzahlen nachlädt. */
const KENNZAHL_SPALTEN = new Set(['progressPercent', 'salesTotal', 'costPlanned', 'costActual', 'costShare', 'margin', 'trafficLights'])

// Die Nummer steht als feste erste Spalte links — in der Vorgabe der
// konfigurierbaren Spalten wäre sie ein zweites Mal da (bis Phase C so gewesen).
const DEFAULT_COLUMN_KEYS = [
  'name',
  'client',
  'status',
  'completionDueAt',
  'positionCount',
]

/**
 * Vorbelegung der FilterBar: bewusst OHNE Datumsfelder — ein Projekt ist ein
 * Stammdatensatz, kein Ereignis zu einem Zeitpunkt (es gibt hier auch keine).
 */
const DEFAULT_FILTER_FIELD_KEYS = ['search', 'status']

/**
 * Detailblatt 1 5.7 — die Liste startet ohne archivierte, verlorene und
 * stornierte (K-5, Auftraggeber 18.9.2026). Nur solange der Benutzer hier noch
 * nie gefiltert hat; danach gilt seine eigene Wahl.
 */
const DEFAULT_FILTER_VALUES: Record<string, ActiveFilter> = {
  status: { fieldKey: 'status', operator: 'is_not', value: 'CANCELLED' },
}

/** Die aktiven Filter als Abfrageparameter — Liste und Export filtern gleich. */
function filterParams(filters: Record<string, ActiveFilter>): ListProjectsParams {
  const params: ListProjectsParams = {}
  for (const [key, active] of Object.entries(filters)) {
    if (!active.value) continue
    if (key === 'search')         params.search         = active.value
    // KA-17: Operator berücksichtigen — „ist nicht Aktiv" filterte wie „ist Aktiv"
    if (key === 'status') {
      if (active.operator === 'is_not') params.statusNot = active.value
      else params.status = active.value
    }
    if (key === 'externalSource') params.externalSource = active.value
    if (key === 'type') {
      if (active.operator === 'is_not') params.typeNot = active.value
      else params.type = active.value
    }
    if (key === 'overdue')   params.overdue   = active.value === 'true'
    if (key === 'templates') params.templates = active.value === 'true'
    if (key === 'clientName')     params.clientName        = active.value
    if (key === 'projectLead')    params.projectLeadUserId = active.value
    if (key === 'site')           params.siteId            = active.value
    if (key === 'completionFrom') params.completionFrom    = active.value.slice(0, 10)
    if (key === 'completionTo')   params.completionTo      = active.value.slice(0, 10)
  }
  return params
}

// ─── Spaltenlayout (AP-23: Cache + Server) ────────────────────────────────────

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

// AP-23: synchron aus dem Cache lesen, Cache + API (debounced) schreiben.
function loadColumnState(): ColumnState {
  const parsed = loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null)
  if (parsed?.columnKeys?.length) return {
    columnKeys: parsed.columnKeys,
    columnWidths: parsed.columnWidths ?? {},
    sortKey: parsed.sortKey ?? 'projectNumber',
    sortDir: parsed.sortDir ?? 'asc',
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'projectNumber', sortDir: 'asc' }
}

function saveColumnState(state: ColumnState) {
  saveUiSetting(COLUMN_LAYOUT_KEY, state)
}

// ─── Status-Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_ANZEIGE[status as ProjectStatus] ?? { label: status, class: 'bg-muted text-muted-foreground' }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${c.class}`}>
      {c.label}
    </span>
  )
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function ProjectsPage() {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const { t } = useTranslation()
  const { user, hasModule } = useAuthStore()
  const preise = erfuelltRecht('projects.price.view', user?.permissions)
  const darf: Darf = {
    verkauf: preise && hasModule('PROJECT_MGMT.BILLING'),
    kosten: preise && hasModule('PROJECT_MGMT.CONTROLLING') && erfuelltRecht('projects.controlling.read', user?.permissions),
  }
  const allColumns = buildColumns(terms, darf)
  const darfExport = erfuelltRecht('projects.export.run', user?.permissions)

  const [users, setUsers] = useState<LookupUser[]>([])
  const [sites, setSites] = useState<LookupSite[]>([])
  const [kennzahlen, setKennzahlen] = useState<Record<string, ProjectListFigures>>({})
  useEffect(() => {
    // Auswahlen der Filter — ein Fehler hier lässt die Liste stehen, nur die Auswahl bleibt leer.
    fetchUsers().then(setUsers).catch(() => setUsers([]))
    fetchSites().then(setSites).catch(() => setSites([]))
  }, [])
  const filterFields = useMemo(() => buildFilterFields(terms, users, sites), [terms, users, sites])

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const dragKeyRef = useRef<string | null>(null)

  const navigate = useNavigate()
  const darfAendern = erfuelltRecht('projects.project.manage', user?.permissions)
  const [anlegen, setAnlegen] = useState(false)
  const [result, setResult] = useState<PaginatedProjects | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Daten laden ──────────────────────────────────────────────────────────────

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, currentPage: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        // KA-17: Die Sortierung wird jetzt tatsächlich mitgeschickt. Vorher war
        // `state` ein ungenutzter Parameter — der Pfeil in der Spaltenüberschrift
        // drehte sich, die Liste blieb nach Projektnummer sortiert.
        const params: ListProjectsParams = {
          ...filterParams(filters),
          page: currentPage,
          limit: 50,
          sortBy: state.sortKey,
          sortDir: state.sortDir,
        }

        const data = await fetchProjects(params)
        setResult(data)
        // AP-35 Nachtrag 5: Nach dieser Ansicht blättert der Detailkopf.
        rememberListContext(user?.id, { ...filterParams(filters), sortBy: state.sortKey, sortDir: state.sortDir })
        // Phase K: Kennzahlen nur für die sichtbaren Zeilen und nur, wenn eine
        // Kennzahl-Spalte eingeblendet ist — je Zeile rechnet der Server die
        // Nachkalkulation.
        const mitKennzahlen = state.columnKeys.some((k) => KENNZAHL_SPALTEN.has(k))
        if (mitKennzahlen && data.data.length) {
          fetchProjectListFigures(data.data.map((p) => p.id))
            .then((r) => setKennzahlen(r.data))
            .catch(() => setKennzahlen({}))
        } else {
          setKennzahlen({})
        }
      } catch (err) {
        setError(`Fehler beim Laden der ${terms.projectPlural}.`)
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    // AP-16: Der Begriff steckt in der Fehlermeldung — nach dem Umschalten
    // muss sie neu gebildet werden.
    [terms.projectPlural, user?.id],
  )

  // Initiales Laden
  useEffect(() => {
    fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  // ── Filter-Änderung ──────────────────────────────────────────────────────────

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => fetchData(newValues, 1, colState), 400)
    },
    [fetchData, colState],
  )

  // ── Spalten speichern ────────────────────────────────────────────────────────

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveColumnState(next)
      return next
    })
  }, [])

  // ── Sortierung ───────────────────────────────────────────────────────────────

  const handleSort = (key: string) => {
    // Neu laden mit dem NEUEN Zustand: `colState` aus der Closure ist hier noch
    // der alte, das Nachladen lief sonst immer eine Sortierung hinterher (KA-17).
    const newDir = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: newDir as 'asc' | 'desc' }
    updateColState(() => next)
    setPage(1)
    fetchData(filterValues, 1, next)
  }

  // ── Drag & Drop für Spalten ───────────────────────────────────────────────

  const handleDragStart = (key: string) => { dragKeyRef.current = key }
  const handleDragOver  = (e: React.DragEvent, key: string) => { e.preventDefault(); setDragOverKey(key) }
  const handleDragEnd   = () => { dragKeyRef.current = null; setDragOverKey(null) }

  const handleDrop = (targetKey: string) => {
    const srcKey = dragKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to   = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    setDragOverKey(null)
  }

  // ── Spalten-Resize ────────────────────────────────────────────────────────

  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null)

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    resizeRef.current = { key, startX: e.clientX, startW }

    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = ev.clientX - resizeRef.current.startX
      const newW  = Math.max(60, resizeRef.current.startW + delta)
      updateColState((prev) => ({
        ...prev,
        columnWidths: { ...prev.columnWidths, [resizeRef.current!.key]: newW },
      }))
    }
    const onUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // ── Sichtbare Spalten ─────────────────────────────────────────────────────

  const shownColumns = colState.columnKeys
    .filter((k) => k !== 'projectNumber')
    .map((k) => allColumns.find((c) => c.key === k))
    .filter((c): c is ColDef => c !== undefined)

  // Die Nummer steht fest vorn — sie wird nicht noch einmal angeboten (Phase K).
  const availableToAdd = allColumns.filter(
    (c) => c.key !== 'projectNumber' && !colState.columnKeys.includes(c.key),
  )

  // ── Zelle rendern ─────────────────────────────────────────────────────────

  const renderCell = (row: Project, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'projectNumber': return <span className="font-mono">{row.projectNumber}</span>
      case 'name':          return row.name
      case 'type':          return PROJECT_TYPE_LABELS[row.type ?? 'ORDER']?.(terms) ?? row.type
      case 'client':        return row.client?.name ?? '—'
      case 'status':        return <StatusBadge status={row.status} />
      case 'projectLead':   return row.projectLeadName ?? '—'
      case 'completionDueAt':
        return (
          <span className={istUeberfaellig(row) ? 'font-medium text-destructive' : undefined}>
            {alsTag(row.completionDueAt)}
          </span>
        )
      case 'positionCount': return row._count?.positions ?? 0
      case 'site':          return row.site?.name ?? '—'
      case 'progressPercent': {
        const k = kennzahlen[row.id]
        return k ? `${k.progressPercent} %` : '…'
      }
      case 'salesTotal':    return kennzahlen[row.id] ? geld(kennzahlen[row.id].salesTotal) : '…'
      case 'costPlanned':   return kennzahlen[row.id] ? geld(kennzahlen[row.id].costPlanned) : '…'
      case 'costActual':    return kennzahlen[row.id] ? geld(kennzahlen[row.id].costActual) : '…'
      case 'costShare': {
        const k = kennzahlen[row.id]
        return k ? (k.costShare == null ? '—' : `${k.costShare} %`) : '…'
      }
      case 'margin':        return kennzahlen[row.id] ? geld(kennzahlen[row.id].margin) : '…'
      case 'trafficLights': {
        const k = kennzahlen[row.id]
        if (!k) return '…'
        return (
          <span className="inline-flex gap-1">
            {AMPEL_TITEL.map(([key, titel]) => (
              <span
                key={key}
                className={`inline-block h-2.5 w-2.5 rounded-full ${TRAFFIC_LIGHT_CLASSES[k.trafficLights[key].value] ?? ''}`}
                title={`${titel}: ${TRAFFIC_LIGHT_REASONS[k.trafficLights[key].reason] ?? k.trafficLights[key].reason}`}
              />
            ))}
          </span>
        )
      }
      case 'customerOrderNumber': return row.customerOrderNumber ?? '—'
      case 'legacyNumber':  return row.legacyNumber ?? '—'
      case 'externalSource':return row.externalSource ?? '—'
      case 'createdAt':     return alsTag(row.createdAt)
      default:              return null
    }
  }

  // ── Paginierung ────────────────────────────────────────────────────────────

  const meta      = result?.meta
  const rows      = result?.data ?? []
  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length > 0 ? 1 : 0) + 1

  return (
    <div className={pageWide}>
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{terms.projectPlural}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Nummer, Kunde, Status, Termine und Positionen — ein Klick öffnet die Detailseite.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {darfExport && (
            <ExportButton
              request={(format, orientation) =>
                projectsExportRequest(
                  { ...filterParams(filterValues), sortBy: colState.sortKey, sortDir: colState.sortDir, format, orientation },
                  // Die Nummer ist die feste erste Spalte der Seite und gehört in jeden Export.
                  [allColumns[0], ...shownColumns].map((c) => ({ key: c.key, label: c.label })),
                )
              }
              mail={{
                source: 'EXPORT_PROJECTS',
                title: t('mailExport.titles.EXPORT_PROJECTS', { projectPlural: terms.projectPlural }),
                subject: terms.projectPlural,
              }}
            />
          )}
          {darfAendern && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
              onClick={() => setAnlegen(true)}
            >
              <Plus className="h-4 w-4" /> Neu
            </button>
          )}
        </div>
      </div>

      {/* FilterBar */}
      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="projects"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
        defaultValues={DEFAULT_FILTER_VALUES}
      />

      {/* Tabellen-Card */}
      <div className="relative rounded-lg border border-border bg-card shadow-sm overflow-hidden">

        {/* Zahnrad-Button */}
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>

        {/* Hilfe „?" – links neben dem Spalten-Zahnrad */}
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {/* AP-23 Phase D: Zurücksetzen / als Mandanten-Standard speichern */}
        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
                    onReset={(value) => {
                const next = (value as ColumnState | null) ?? {
                  columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {},
                  sortKey: 'projectNumber', sortDir: 'asc' as const,
                }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
                fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {/* Fehler */}
        {error && (
          <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Fixe erste Spalte: Projektnr */}
                <th
                  className="relative px-4 py-1.5 text-left font-medium text-foreground"
                  style={colState.columnWidths['projectNumber'] ? { width: colState.columnWidths['projectNumber'] } : undefined}
                >
                  <button
                    type="button"
                    onClick={() => handleSort('projectNumber')}
                    className="flex items-center gap-1 hover:text-primary"
                  >
                    {allColumns[0].label}
                    {colState.sortKey === 'projectNumber'
                      ? colState.sortDir === 'asc'
                        ? <ArrowUp className="h-3 w-3 text-primary" />
                        : <ArrowDown className="h-3 w-3 text-primary" />
                      : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </button>
                  <div
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                    onMouseDown={(e) => handleResizeStart(e, 'projectNumber')}
                  />
                </th>

                {/* Konfigurierbare Spalten */}
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground
                      ${col.align === 'right' ? 'text-right' : 'text-left'}
                      ${columnConfigMode ? 'cursor-grab' : ''}
                      ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          className="flex items-center gap-1 hover:text-primary"
                        >
                          {col.label}
                          {colState.sortKey === col.key
                            ? colState.sortDir === 'asc'
                              ? <ArrowUp className="h-3 w-3 text-primary" />
                              : <ArrowDown className="h-3 w-3 text-primary" />
                            : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() => updateColState((prev) => ({
                            ...prev,
                            columnKeys: prev.columnKeys.filter((k) => k !== col.key),
                          }))}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title="Spalte entfernen"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                    />
                  </th>
                ))}

                {/* + Spalte hinzufügen */}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        const next = { ...colState, columnKeys: [...colState.columnKeys, key] }
                        updateColState(() => next)
                        if (KENNZAHL_SPALTEN.has(key)) fetchData(filterValues, page, next)
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                  </th>
                )}

                <th className="w-4" />
              </tr>
            </thead>

            <tbody>
              {/* Laden */}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-muted-foreground text-sm">
                    Laden…
                  </td>
                </tr>
              )}

              {/* Leer */}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-muted-foreground text-sm">
                    {`Keine ${terms.projectPlural} gefunden.`}
                  </td>
                </tr>
              )}

              {/* Datenzeilen */}
              {rows.map((row, idx) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/projects/${row.id}`)}
                  className={`cursor-pointer border-b border-border last:border-0 ${
                    idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  } hover:bg-accent/30 transition-colors`}
                >
                  {/* Projektnr. (fix) */}
                  <td className="px-4 py-1.5 font-mono whitespace-nowrap text-muted-foreground">
                    {row.projectNumber}
                  </td>

                  {/* Konfigurierbare Spalten */}
                  {shownColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-1.5 whitespace-nowrap ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}
                    >
                      {renderCell(row, col)}
                    </td>
                  ))}

                  <td />
                </tr>
              ))}

              {/* Summenzeile */}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="px-4 py-1.5 text-muted-foreground whitespace-nowrap">
                    {loading
                      ? 'Laden…'
                      : meta
                        ? `${meta.total} ${terms.projectPlural}`
                        : `${rows.length} ${terms.projectPlural}`}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="px-4 py-1.5" />
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginierung */}
        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              Seite {meta.page} von {meta.totalPages} ({meta.total} Einträge)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ‹
              </button>
              <button
                type="button"
                disabled={page >= meta.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info-Box: Schreibschutz */}
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        <ExternalLink size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          {`Angelegt, gepflegt und abgerechnet werden ${terms.projectPlural} hier. Was aus einem
          Fremdsystem kommt, trägt eine Herkunft — die Spalte lässt sich einblenden.`}
        </span>
      </div>
      {anlegen && (
        <ProjectFormDialog
          onClose={() => setAnlegen(false)}
          onSaved={(p) => {
            setAnlegen(false)
            // Weiter, wo der Dialog aufgehört hat: die Projektdaten mit dem
            // Fokus im ersten leeren Feld (AP-36 4.2).
            navigate(`/projects/${p.id}?tab=data&neu=1`)
          }}
        />
      )}
    </div>
  )
}
